import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Cropper from 'react-easy-crop';
import { notifications } from '../../lib/notifications';
import {
    IconCamera,
    IconPhoto,
    IconSend,
    IconCheck,
    IconX,
    IconRotateClockwise,
    IconCrop,
} from '@tabler/icons-react';
import qrSessionService from '../../services/qrSessionService';

// Canvas crop helper — same logic as browser GenerateImage.jsx
const getCroppedBlob = (imageSrc, croppedAreaPixels, rotation) => {
    return new Promise((resolve, reject) => {
        const image = new window.Image();
        image.onload = () => {
            const { width: cropW, height: cropH } = croppedAreaPixels;

            // Step 1: draw the rotated crop at native pixel size
            const rawCanvas = document.createElement('canvas');
            rawCanvas.width  = cropW;
            rawCanvas.height = cropH;
            const rawCtx = rawCanvas.getContext('2d');
            const rad = (rotation * Math.PI) / 180;
            const cx  = croppedAreaPixels.x + cropW / 2;
            const cy  = croppedAreaPixels.y + cropH / 2;
            rawCtx.translate(cropW / 2, cropH / 2);
            rawCtx.rotate(rad);
            rawCtx.translate(-cx, -cy);
            rawCtx.drawImage(image, 0, 0);

            // Step 2: scale to fixed output 836×1024 (same as browser)
            const outCanvas = document.createElement('canvas');
            outCanvas.width  = 836;
            outCanvas.height = 1024;
            outCanvas.getContext('2d').drawImage(rawCanvas, 0, 0, 836, 1024);

            outCanvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('toBlob failed')), 'image/jpeg', 0.92);
        };
        image.onerror = reject;
        image.src = imageSrc;
    });
};

// Convert blob → base64 + dataURL
const blobToBase64 = (blob) => new Promise((resolve) => {
    const fr = new FileReader();
    fr.onload = e => {
        const dataUrl = e.target.result;
        resolve({ base64: dataUrl.split(',')[1], mimeType: 'image/jpeg', previewUrl: dataUrl });
    };
    fr.readAsDataURL(blob);
});

// ─────────────────────────────────────────────────────────────────────────────

export default function MobileUpload() {
    const { token } = useParams();
    const { t } = useTranslation();

    const [validating, setValidating]   = useState(true);
    const [isValid, setIsValid]         = useState(false);
    const [mode, setMode]               = useState('single');
    const [expiresAt, setExpiresAt]     = useState(null);
    const [timeLeft, setTimeLeft]       = useState(null);

    // Compressed image ready to send
    const [previewUrl, setPreviewUrl]           = useState(null);
    const [selectedBase64, setSelectedBase64]   = useState(null);
    const [selectedMimeType, setSelectedMimeType] = useState(null);

    const [sending, setSending]       = useState(false);
    const [confirming, setConfirming] = useState(false);
    const [sent, setSent]             = useState(false);

    // Dual-specific
    const [dualStep, setDualStep] = useState('left');

    // ── Crop state ────────────────────────────────────────────────────────────
    const [cropStep, setCropStep]                 = useState(false);
    const [cropImageSrc, setCropImageSrc]         = useState(null);
    const [cropArea, setCropArea]                 = useState({ x: 0, y: 0 });
    const [cropZoom, setCropZoom]                 = useState(1);
    const [cropRotation, setCropRotation]         = useState(0);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [applyingCrop, setApplyingCrop]         = useState(false);

    const cameraInputRef  = useRef(null);
    const galleryInputRef = useRef(null);
    const cropObjectUrlRef = useRef(null);

    // Prevent native pinch-zoom (react-easy-crop handles its own zoom)
    useEffect(() => {
        const viewport = document.querySelector('meta[name="viewport"]');
        const original = viewport?.getAttribute('content');
        viewport?.setAttribute('content', 'width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no');
        return () => { if (original && viewport) viewport.setAttribute('content', original); };
    }, []);

    // Validate token
    useEffect(() => {
        const validate = async () => {
            try {
                const res = await qrSessionService.validateToken(token);
                if (res?.data?.valid) {
                    setIsValid(true);
                    setMode(res.data.mode || 'single');
                    setExpiresAt(new Date(res.data.expiresAt).getTime());
                    if (res.data.status === 'partial') setDualStep('right');
                } else {
                    setIsValid(false);
                }
            } catch {
                setIsValid(false);
            } finally {
                setValidating(false);
            }
        };
        validate();
    }, [token]);

    // Countdown timer
    useEffect(() => {
        if (!expiresAt) return;
        const tick = () => {
            const remaining = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
            setTimeLeft(remaining);
            if (remaining === 0) setIsValid(false);
        };
        tick();
        const interval = setInterval(tick, 1000);
        return () => clearInterval(interval);
    }, [expiresAt]);

    const formatTime = (seconds) => {
        const m = Math.floor(seconds / 60).toString().padStart(2, '0');
        const s = (seconds % 60).toString().padStart(2, '0');
        return `${m}:${s}`;
    };

    const MAX_FILE_SIZE = 10 * 1024 * 1024;

    // ── File selected → open crop UI ─────────────────────────────────────────
    const handleFileSelected = (file) => {
        if (!file || !file.type.startsWith('image/')) return;
        if (file.size > MAX_FILE_SIZE) {
            notifications.show({ title: t('mobileUpload.fileTooLargeTitle'), message: t('mobileUpload.fileTooLargeBody'), color: 'red' });
            return;
        }
        // Revoke any previous object URL
        if (cropObjectUrlRef.current) URL.revokeObjectURL(cropObjectUrlRef.current);
        const objectUrl = URL.createObjectURL(file);
        cropObjectUrlRef.current = objectUrl;

        setCropImageSrc(objectUrl);
        setCropArea({ x: 0, y: 0 });
        setCropZoom(1);
        setCropRotation(0);
        setCroppedAreaPixels(null);
        setCropStep(true);
    };

    const handleCameraCapture = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) handleFileSelected(file);
    };

    const handleGallerySelect = (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (file) handleFileSelected(file);
    };

    const clearSelection = () => {
        setPreviewUrl(null);
        setSelectedBase64(null);
        setSelectedMimeType(null);
    };

    // ── Apply crop ────────────────────────────────────────────────────────────
    const applyCrop = useCallback(async () => {
        if (!croppedAreaPixels || !cropImageSrc) return;
        setApplyingCrop(true);
        try {
            const blob   = await getCroppedBlob(cropImageSrc, croppedAreaPixels, cropRotation);
            const result = await blobToBase64(blob);

            if (cropObjectUrlRef.current) {
                URL.revokeObjectURL(cropObjectUrlRef.current);
                cropObjectUrlRef.current = null;
            }
            setCropStep(false);
            setCropImageSrc(null);

            setSelectedBase64(result.base64);
            setSelectedMimeType(result.mimeType);
            setPreviewUrl(result.previewUrl);
        } catch {
            notifications.show({ title: t('common.error') || 'Error', message: t('mobileUpload.cropFailed') || 'Crop failed, please try again.', color: 'red' });
        } finally {
            setApplyingCrop(false);
        }
    }, [croppedAreaPixels, cropImageSrc, cropRotation, t]);

    const cancelCrop = () => {
        if (cropObjectUrlRef.current) {
            URL.revokeObjectURL(cropObjectUrlRef.current);
            cropObjectUrlRef.current = null;
        }
        setCropStep(false);
        setCropImageSrc(null);
        setCroppedAreaPixels(null);
    };

    // ── Send ──────────────────────────────────────────────────────────────────
    const handleSend = async () => {
        if (!selectedBase64 || sending || sent || confirming) return;
        setSending(true);
        try {
            await qrSessionService.uploadImage(token, selectedBase64, selectedMimeType);
            setSending(false);
            setConfirming(true);
            const deadline = Date.now() + 30000;
            const pollInterval = setInterval(async () => {
                try {
                    const res = await qrSessionService.checkConsumed(token);
                    if (res?.data?.consumed || Date.now() > deadline) {
                        clearInterval(pollInterval);
                        setConfirming(false);
                        setSent(true);
                    }
                } catch { /* keep waiting */ }
            }, 2000);
        } catch {
            setSending(false);
            setIsValid(false);
        }
    };

    const handleSendDual = async () => {
        if (!selectedBase64 || sending) return;
        setSending(true);
        try {
            const res = await qrSessionService.uploadDualSide(token, dualStep, selectedBase64, selectedMimeType);
            setSending(false);
            if (dualStep === 'left') {
                setDualStep('right');
                clearSelection();
            } else {
                setConfirming(true);
                const deadline = Date.now() + 30000;
                const pollInterval = setInterval(async () => {
                    try {
                        const check = await qrSessionService.checkConsumed(token);
                        if (check?.data?.consumed || Date.now() > deadline) {
                            clearInterval(pollInterval);
                            setConfirming(false);
                            setSent(true);
                        }
                    } catch { /* keep waiting */ }
                }, 2000);
                if (res?.data?.status === 'received') setConfirming(true);
            }
        } catch {
            setSending(false);
            setIsValid(false);
        }
    };

    // ── Validating ────────────────────────────────────────────────────────────
    if (validating) {
        return (
            <div className="flex items-center justify-center min-h-[100dvh] bg-white dark:bg-zinc-900">
                <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" />
                    <p className="text-sm text-zinc-500 dark:text-zinc-400">{t('mobileUpload.checking')}</p>
                </div>
            </div>
        );
    }

    // ── Expired / Invalid ─────────────────────────────────────────────────────
    if (!isValid) {
        return (
            <div className="min-h-[100dvh] bg-white dark:bg-zinc-900 p-6 flex flex-col">
                <div className="mb-8">
                    <img src="/images/logo.svg" alt="logo" className="w-28" />
                </div>
                <div className="flex items-center justify-center flex-1">
                    <div className="flex flex-col items-center gap-4 max-w-[360px] w-full">
                        <div className="text-[56px] text-center">⏱️</div>
                        <h2 className="text-xl font-bold text-center text-zinc-900 dark:text-zinc-100">{t('mobileUpload.expiredTitle')}</h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center">{t('mobileUpload.expiredBody')}</p>
                        <button
                            className="mt-4 px-4 py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 font-medium text-sm transition-colors inline-flex items-center justify-center gap-2 w-full"
                            onClick={() => window.close()}
                        >
                            {t('mobileUpload.closeTab')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Confirming ────────────────────────────────────────────────────────────
    if (confirming) {
        return (
            <div className="min-h-[100dvh] bg-white dark:bg-zinc-900 p-6 flex flex-col">
                <div className="mb-8">
                    <img src="/images/logo.svg" alt="logo" className="w-28" />
                </div>
                <div className="flex items-center justify-center flex-1">
                    <div className="flex flex-col items-center gap-6 max-w-[360px] w-full">
                        {/* dots-style loader */}
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce [animation-delay:-0.3s]" />
                            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce [animation-delay:-0.15s]" />
                            <div className="w-3 h-3 bg-orange-500 rounded-full animate-bounce" />
                        </div>
                        <h2 className="text-xl font-bold text-center text-zinc-900 dark:text-zinc-100">{t('mobileUpload.confirmingTitle')}</h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center">{t('mobileUpload.confirmingBody')}</p>
                    </div>
                </div>
            </div>
        );
    }

    // ── Sent / Done ───────────────────────────────────────────────────────────
    if (sent) {
        return (
            <div className="min-h-[100dvh] bg-white dark:bg-zinc-900 p-6 flex flex-col">
                <div className="mb-8">
                    <img src="/images/logo.svg" alt="logo" className="w-28" />
                </div>
                <div className="flex items-center justify-center flex-1">
                    <div className="flex flex-col items-center gap-4 max-w-[360px] w-full">
                        <div className="w-20 h-20 rounded-full bg-[#2f9e44] flex items-center justify-center">
                            <IconCheck size={40} color="white" />
                        </div>
                        <h2 className="text-xl font-bold text-center text-zinc-900 dark:text-zinc-100">{t('mobileUpload.sentTitle')}</h2>
                        <p className="text-sm text-zinc-500 dark:text-zinc-400 text-center">{t('mobileUpload.sentBody')}</p>
                        <button
                            className="mt-4 px-4 py-2.5 rounded-xl bg-zinc-100 hover:bg-zinc-200 dark:bg-zinc-700 dark:hover:bg-zinc-600 text-zinc-700 dark:text-zinc-200 font-medium text-sm transition-colors inline-flex items-center justify-center gap-2 w-full"
                            onClick={() => window.close()}
                        >
                            {t('mobileUpload.closeTab')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Crop UI (full-screen, mandatory) ──────────────────────────────────────
    if (cropStep && cropImageSrc) {
        return (
            <div className="fixed inset-0 z-50 bg-black flex flex-col" style={{ touchAction: 'none' }}>
                {/* Header */}
                <div
                    className="flex items-center gap-2 z-10"
                    style={{
                        padding: '12px 16px',
                        paddingTop: 'max(12px, env(safe-area-inset-top))',
                        background: 'rgba(0,0,0,0.7)',
                    }}
                >
                    <IconCrop size={18} color="#fe9a00" />
                    <span className="text-sm font-semibold text-white">{t('mobileUpload.cropTitle') || 'Crop Photo'}</span>
                    <span className="text-xs text-zinc-400 ml-auto">
                        {t('mobileUpload.cropHint') || 'Pinch to zoom · Drag to move'}
                    </span>
                </div>

                {/* Cropper area */}
                <div className="flex-1 relative">
                    <Cropper
                        image={cropImageSrc}
                        crop={cropArea}
                        zoom={cropZoom}
                        rotation={cropRotation}
                        aspect={209 / 256}
                        onCropChange={setCropArea}
                        onZoomChange={setCropZoom}
                        onCropComplete={(_, pixels) => setCroppedAreaPixels(pixels)}
                        style={{
                            containerStyle: { background: '#111' },
                            cropAreaStyle: { border: '2px solid #fe9a00', color: 'rgba(0,0,0,0.55)' },
                        }}
                        showGrid={false}
                        minZoom={0.5}
                        maxZoom={4}
                        zoomSpeed={0.3}
                    />
                </div>

                {/* Bottom toolbar */}
                <div
                    className="bg-[#111]"
                    style={{
                        padding: '14px 16px',
                        paddingBottom: 'max(16px, env(safe-area-inset-bottom))',
                    }}
                >
                    {/* Rotate row */}
                    <div className="flex items-center justify-center gap-8 mb-3.5">
                        {/* Rotate CCW */}
                        <div className="flex flex-col items-center gap-1">
                            <button
                                className="w-[52px] h-[52px] rounded-[14px] bg-zinc-700 hover:bg-zinc-600 text-zinc-200 flex items-center justify-center transition-colors"
                                onClick={() => setCropRotation(r => (r - 90 + 360) % 360)}
                            >
                                <IconRotateClockwise size={26} style={{ transform: 'scaleX(-1)' }} />
                            </button>
                            <span className="text-[10px] text-zinc-400">{t('mobileUpload.rotateCCW') || 'Rotate ↺'}</span>
                        </div>

                        {/* Rotation indicator */}
                        <div className="flex flex-col items-center gap-0.5 min-w-[44px]">
                            <span className="text-lg font-bold text-white">{cropRotation}°</span>
                            <span className="text-[10px] text-zinc-400">{t('mobileUpload.rotation') || 'Rotation'}</span>
                        </div>

                        {/* Rotate CW */}
                        <div className="flex flex-col items-center gap-1">
                            <button
                                className="w-[52px] h-[52px] rounded-[14px] bg-zinc-700 hover:bg-zinc-600 text-zinc-200 flex items-center justify-center transition-colors"
                                onClick={() => setCropRotation(r => (r + 90) % 360)}
                            >
                                <IconRotateClockwise size={26} />
                            </button>
                            <span className="text-[10px] text-zinc-400">{t('mobileUpload.rotateCW') || 'Rotate ↻'}</span>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex items-center gap-2.5">
                        <button
                            className="flex-1 px-4 py-2.5 rounded-xl bg-zinc-700 hover:bg-zinc-600 text-zinc-200 font-medium text-sm transition-colors inline-flex items-center justify-center gap-2"
                            onClick={cancelCrop}
                        >
                            <IconX size={16} />
                            {t('common.cancel') || 'Cancel'}
                        </button>
                        <button
                            className="flex-[2] px-4 py-2.5 rounded-xl bg-orange-500 hover:bg-orange-600 text-white font-medium text-sm transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2"
                            disabled={!croppedAreaPixels || applyingCrop}
                            onClick={applyCrop}
                        >
                            {applyingCrop
                                ? <span className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <IconCrop size={16} />
                            }
                            {t('mobileUpload.cropConfirm') || 'Crop & Continue'}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ── Main upload UI ────────────────────────────────────────────────────────
    const isDual      = mode === 'dual';
    const stepColor   = isDual ? (dualStep === 'left' ? '#fe9a00' : '#3b82f6') : null;
    const stepTitle   = isDual
        ? (dualStep === 'left'
            ? t('mobileUpload.dualStep1Title', 'প্রথম ব্যক্তি বাম পাশ')
            : t('mobileUpload.dualStep2Title', 'দ্বিতীয় ব্যক্তি ডান পাশ'))
        : null;
    const instruction = isDual
        ? (dualStep === 'left'
            ? t('mobileUpload.dualStep1Instruction', 'বাম পাশের ব্যক্তির ছবি তুলুন বা গ্যালারি থেকে বেছে নিন।')
            : t('mobileUpload.dualStep2Instruction', 'ডান পাশের ব্যক্তির ছবি তুলুন বা গ্যালারি থেকে বেছে নিন।'))
        : t('mobileUpload.instruction');
    const onSend  = isDual ? handleSendDual : handleSend;
    const hasImage = !!selectedBase64;

    return (
        <div className="min-h-[100dvh] bg-white dark:bg-zinc-900 flex flex-col p-4 pb-8">
            {/* Header */}
            <div className="mb-4">
                <div className="flex items-center justify-between">
                    <img src="/images/logo.svg" alt="logo" className="w-28" />
                    {timeLeft !== null && (
                        <span
                            className={`text-sm font-semibold ${timeLeft < 60 ? 'text-red-500' : 'text-zinc-500 dark:text-zinc-400'}`}
                            style={{ fontVariantNumeric: 'tabular-nums' }}
                        >
                            ⏱ {formatTime(timeLeft)}
                        </span>
                    )}
                </div>

                {isDual && (
                    <div className="flex flex-col items-center gap-1.5 mt-3">
                        <div className="flex items-center gap-1.5 justify-center">
                            <div className="w-9 h-1.5 rounded-full bg-[#fe9a00]" />
                            <div
                                className="w-9 h-1.5 rounded-full transition-colors"
                                style={{ background: dualStep === 'right' ? '#3b82f6' : '#dde1e7' }}
                            />
                        </div>
                        <span className="text-base font-bold text-center" style={{ color: stepColor }}>{stepTitle}</span>
                        <span className="text-xs text-zinc-500 dark:text-zinc-400 text-center">
                            {dualStep === 'left'
                                ? t('mobileUpload.dualStepProgress1', 'ধাপ ১/২')
                                : t('mobileUpload.dualStepProgress2', 'ধাপ ২/২')}
                        </span>
                    </div>
                )}
                {!isDual && (
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 mt-1">{t('mobileUpload.subtitle')}</p>
                )}
            </div>

            {/* Face placeholder */}
            {!hasImage && (
                <div className="flex flex-1 flex-col items-center justify-center gap-3">
                    <img
                        src="/images/face-placeholder.webp"
                        alt=""
                        onClick={() => galleryInputRef.current?.click()}
                        style={{ width: 260, height: 315, objectFit: 'cover', borderRadius: 16, display: 'block', cursor: 'pointer' }}
                    />
                    <p className="text-xs text-zinc-500 dark:text-zinc-400 text-center max-w-[240px]">{instruction}</p>
                </div>
            )}

            {/* Image preview (after crop) */}
            {hasImage && previewUrl && (
                <div
                    className="flex-1 relative rounded-xl overflow-hidden bg-black flex items-center justify-center mb-3"
                    style={{ maxHeight: '60dvh' }}
                >
                    <img src={previewUrl} alt="preview" style={{ maxWidth: '100%', maxHeight: '60dvh', objectFit: 'contain', display: 'block' }} />
                    <button
                        onClick={clearSelection}
                        aria-label="Remove image"
                        className="absolute top-2 right-2 p-1.5 rounded-full bg-black/60 text-white flex items-center justify-center"
                    >
                        <IconX size={18} />
                    </button>
                </div>
            )}

            {/* Action buttons */}
            <div className="flex flex-col gap-3 mt-auto pt-4">
                {!hasImage ? (
                    <>
                        <button
                            className={`px-4 py-3 rounded-xl font-medium text-sm transition-colors inline-flex items-center justify-center gap-2 w-full text-white ${isDual && dualStep === 'right' ? 'bg-blue-500 hover:bg-blue-600' : 'bg-orange-500 hover:bg-orange-600'}`}
                            onClick={() => cameraInputRef.current?.click()}
                        >
                            <IconCamera size={20} />
                            {isDual && dualStep === 'right' ? t('mobileUpload.uploadNextPhoto', 'পরের ছবি আপলোড করুন') : t('mobileUpload.takePhoto')}
                        </button>
                        <button
                            className={`px-4 py-3 rounded-xl font-medium text-sm transition-colors inline-flex items-center justify-center gap-2 w-full bg-transparent border ${isDual && dualStep === 'right' ? 'border-blue-500 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-950' : 'border-orange-500 text-orange-500 hover:bg-orange-50 dark:hover:bg-orange-950'}`}
                            onClick={() => galleryInputRef.current?.click()}
                        >
                            <IconPhoto size={20} />
                            {t('mobileUpload.chooseGallery')}
                        </button>
                    </>
                ) : (
                    <>
                        <button
                            className={`px-4 py-3.5 rounded-xl font-bold text-base transition-colors disabled:opacity-50 inline-flex items-center justify-center gap-2 w-full text-white ${isDual && dualStep === 'left' ? 'bg-orange-500 hover:bg-orange-600' : isDual ? 'bg-blue-500 hover:bg-blue-600' : 'bg-green-600 hover:bg-green-700'}`}
                            disabled={sending}
                            onClick={onSend}
                        >
                            {sending
                                ? <span className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                : <IconSend size={22} />
                            }
                            {isDual && dualStep === 'left' ? t('mobileUpload.next') : t('mobileUpload.sendToComputer')}
                        </button>
                        <button
                            className="px-4 py-2.5 rounded-xl text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 font-medium text-sm transition-colors inline-flex items-center justify-center gap-2 w-full"
                            onClick={clearSelection}
                        >
                            {t('mobileUpload.chooseDifferent')}
                        </button>
                    </>
                )}
            </div>

            <input ref={cameraInputRef}  type="file" accept="image/*" capture="environment" style={{ display: 'none' }} onChange={handleCameraCapture} />
            <input ref={galleryInputRef} type="file" accept="image/*"                       style={{ display: 'none' }} onChange={handleGallerySelect} />
        </div>
    );
}
